import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, IMG } from "./ui";
import { breadcrumb } from "./seo";

/* ─── Lifecycle timeline ──────────────────────────────────────────────────── */
const LIFECYCLE: { stage: string; title: string; body: string }[] = [
  {
    stage: "Configure",
    title: "An AI doctor, tuned to your specialty",
    body: "Choose a specialty and clinical model — AI-only or AI with clinician sign-off. Set region and consult length. We bind it to a medical persona, a lifelike video avatar and the right safety guardrails.",
  },
  {
    stage: "Book",
    title: "Patients self-schedule by email",
    body: "A patient picks a time over email — no portal to log into. The visit moves to scheduled with a single-use, secure join link that's valid only inside the appointment window.",
  },
  {
    stage: "Intake",
    title: "Reports become a pre-visit brief",
    body: "The patient securely uploads labs, imaging, DICOM and prior notes, and consent is captured. Our ingestion pipeline extracts the findings and writes a structured pre-visit brief before the call.",
  },
  {
    stage: "Consult",
    title: "A real-time AI video visit",
    body: "The AI doctor joins one link already briefed — recorded to private storage. It reasons over the patient's longitudinal health journal and reacts to live vitals streaming into the room.",
  },
  {
    stage: "Summarize",
    title: "A SOAP summary, written and sent",
    body: "Out comes a SOAP visit summary, triage, next steps and a patient-friendly explanation. The visit is appended to the health journal and the summary is emailed to the patient.",
  },
  {
    stage: "Review & bill",
    title: "Clinician sign-off, then a clear charge",
    body: "Where your model requires it, a licensed clinician reviews and signs the note before it's final. Each completed consult is billed at its locked, duration-based price — never a surprise.",
  },
];

function Lifecycle() {
  return (
    <section className="py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>The lifecycle</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Six stages. Zero manual handoffs.
          </h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {LIFECYCLE.map((s, i) => (
            <div key={s.stage} className="flex gap-5 rounded-3xl border border-warm-border bg-warm-card p-6">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-warm-ink text-sm font-bold text-warm-bg">
                {i + 1}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-warm-accent">{s.stage}</div>
                <h3 className="mt-1 text-lg font-semibold text-warm-ink">{s.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-warm-muted">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── Roles ───────────────────────────────────────────────────────────────── */
function Roles() {
  const roles: { role: string; can: string[] }[] = [
    {
      role: "For your clinic",
      can: [
        "Configure AI doctor personas and specialties",
        "Manage your clinicians and patient roster",
        "Review visit summaries and sign off",
        "Manage billing — pay per completed consult",
      ],
    },
    {
      role: "For patients",
      can: [
        "Self-schedule a consultation by email",
        "Upload labs, imaging and notes securely",
        "Join from the browser with one link",
        "Own your health journal — view, export, delete",
      ],
    },
    {
      role: "For platform admins",
      can: [
        "Configure every provider key in one place",
        "Monitor orgs, usage and billing",
        "Tune the cost-plus pricing model",
        "Full operational visibility and audit",
      ],
    },
  ];
  return (
    <section className="bg-warm-sand/40 py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>Three roles, one platform</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Built for everyone in the loop.
          </h2>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {roles.map((r) => (
            <div key={r.role} className="rounded-3xl border border-warm-border bg-warm-card p-7">
              <h3 className="text-xl font-semibold text-warm-ink">{r.role}</h3>
              <ul className="mt-5 space-y-3">
                {r.can.map((c) => (
                  <li key={c} className="flex gap-3 text-[15px] text-warm-muted">
                    <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-warm-accent/15 text-warm-accent">
                      <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3.5">
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── Security ────────────────────────────────────────────────────────────── */
function Security() {
  const items: [string, string][] = [
    ["Encrypted at rest", "Provider keys are AES-GCM encrypted and never returned to non-admins."],
    ["Tenant & patient isolation", "RBAC scopes every query to your clinic and to the patient — no cross-tenant reads, ever."],
    ["Signed webhooks", "Tavus, Resend and Stripe are signature-verified and idempotent."],
    ["Private recordings", "Consultations live in private object storage, served only through signed, expiring links."],
    ["Consent & audit log", "Telehealth and recording consent is captured up front, backed by an immutable audit log."],
    ["Hardened auth", "PBKDF2-SHA256 passwords, short-lived tokens, MFA and session revocation."],
  ];
  return (
    <section className="py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>Security</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            PHI-grade safeguards from the first visit.
          </h2>
        </div>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-warm-border bg-warm-border sm:grid-cols-2 lg:grid-cols-3">
          {items.map(([t, d]) => (
            <div key={t} className="bg-warm-card p-7">
              <h3 className="font-semibold text-warm-ink">{t}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-warm-muted">{d}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

export function Platform() {
  return (
    <MarketingPage
      seo={{
        title: "Platform — WonderfulDoctor",
        description:
          "One system that schedules, ingests reports, conducts the AI video visit and writes the summary — from a patient's first email to a clinician-signed, source-grounded SOAP note.",
        path: "/platform",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "Platform", path: "/platform" },
        ]),
      }}
    >
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(244,201,155,0.5) 0%, rgba(244,201,155,0) 70%)" }}
        />
        <Container className="relative pt-20 pb-12 text-center sm:pt-28">
          <Eyebrow>The platform</Eyebrow>
          <h1 className="mx-auto mt-7 max-w-3xl text-balance text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            One system that <span className="font-display italic font-normal text-warm-accent">schedules, reads reports, consults and summarizes</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-warm-muted">
            WonderfulDoctor automates the visit coordination layer — from a patient's first email to a
            clinician-signed SOAP summary, with every answer grounded in their own reports and vitals.
          </p>
          <div className="mt-8 flex justify-center">
            <MButton to="/signup" className="px-6 py-3 text-base">
              Start free <ArrowRight />
            </MButton>
          </div>
        </Container>
        <Container className="relative pb-8">
          <div className="overflow-hidden rounded-4xl border border-warm-border shadow-[0_30px_80px_-30px_rgba(36,31,25,0.35)]">
            <img src={IMG.platform} alt="Clinicians reviewing the complete virtual care workflow" className="h-[280px] w-full object-cover sm:h-[400px]" />
          </div>
        </Container>
      </section>
      <Lifecycle />
      <Roles />
      <Security />
    </MarketingPage>
  );
}
